import { Button } from "@newsela/angelou";

import { LANG } from "@/constants/Language/Language";
import { Assignment } from "@/types/models/Assignment.interface";
import ContentLevel from "@/types/models/ContentLevel.interface";
import ActionsDropdown from "@/view/ActionsDropdown";
import getLanguageCode from "@/view/utils/getLanguageCode";
import getLanguageText from "@/view/utils/getLanguageText";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import { TCurrentLevel } from "@/view/utils/useUpdateLevel/useUpdateLevel";
import {
  buttonCss,
  dropdownCss,
  languageInitials,
  languageTitle,
} from "./LanguageSelector.style";
import renderLanguages from "./renderLanguages";

interface LanguageSelectorInterface {
  contentId: string;
  currentLevel: ContentLevel;
  setCurrentLevel: (level: TCurrentLevel) => void;
  isLevelSelectorVisible: boolean;
  sortedLevels: ContentLevel[];
}

export const LanguageSelector = ({
  contentId,
  currentLevel,
  setCurrentLevel,
  isLevelSelectorVisible,
  sortedLevels,
}: LanguageSelectorInterface) => {
  const eventProperties = {
    objectType: "Content",
    actionPrefix: "Initiate",
    actionName: "Read",
    streamProperties: {
      content_type: "Article",
      description: "Toggle language selector.",
      content_id: currentLevel?.id,
    },
  };

  const { dispatch, updatedAssignment } = useAssignmentReducerContext() as {
    dispatch: () => void;
    updatedAssignment: Assignment;
  };

  const currentLanguage = currentLevel?.language;

  const renderLanguageTitle = (lang: LANG) => (
    <>
      <span css={languageTitle}>{getLanguageText(lang)}</span>
      <span css={languageInitials}>{getLanguageCode(lang)?.toUpperCase()}</span>
    </>
  );

  return (
    <ActionsDropdown
      ariaProps={{
        "aria-label": `${getLanguageText(currentLanguage) || currentLanguage}: current language`,
      }}
      buttonFlavor={Button.flavors.muted}
      buttonCss={buttonCss(isLevelSelectorVisible)}
      buttonText={
        currentLanguage ? renderLanguageTitle(currentLanguage) : currentLanguage
      }
      dataProps={{
        "data-qa-selector": `${getLanguageText(currentLanguage) || currentLanguage}: current language`,
      }}
      dropdownCss={dropdownCss}
      eventProperties={eventProperties}
      id={`language-selector-${contentId}`}
      renderDropdownItems={(closeDropdown) =>
        renderLanguages({
          currentLevel,
          setCurrentLevel,
          sortedLevels,
          eventProperties,
          closeDropdown,
          updatedAssignment,
          dispatch,
        })
      }
    />
  );
};

export default LanguageSelector;
